import { ZoneContext } from 'context/ZoneContext';
import { useContext, useEffect } from 'react';
import { TransformComponent } from 'react-zoom-pan-pinch';
import { Map, MapLayout, PathDescriptions, StationsWrapper, TourActiveDrawerAndStationElement } from './Zone.styled';
import Footer from './components/Footer';
import { RenderPaths } from './components/RenderPaths/RenderPaths';
import { Backgrounds, Lables } from './components/Section';
import StationRenderer from './components/Station';
import Actions from './components/Actions';
import { useMapController } from './hooks/useMapController';
import { MapTourContext } from 'context/MapTourContext';
import { TourType } from 'pages/Map/components/MapTour';
import PathDescription from './components/PathRenderer/PathDescription';
import { usePreventZoom } from 'utils/utils';
import { useMapInteractionEvents } from './hooks/useMapEvents';
import { MapSearchContext } from 'context/MapSearchContext';
import { useLocation } from 'react-router-dom';
import { useMapParams } from 'hooks/useMapParams';

export type ZoneSwitchState = {
    isCurrentLocationActive?: boolean;
};

export const Zone = () => {
    const { selectedStation, stations, paths, highestAbsolutePosition, selectedPath, isZoneFirstTimeRender, setIsZoneFirstTimeRender } =
        useContext(ZoneContext);
    const { tour, isCurrentTourItem } = useContext(MapTourContext);
    const { activeSearchId } = useContext(MapSearchContext);
    const { zoomTo, zoomToSearchEle, zoomToCurrentLocation, zoomToSelectedStation } = useMapController();
    usePreventZoom();
    useMapInteractionEvents();
    const location = useLocation();
    const { hasInitialMapParams, initialSelectedStation, setInitialSelectedStation } = useMapParams();
    const isCurrentLocationActive = (location.state as ZoneSwitchState)?.isCurrentLocationActive;

    useEffect(() => {
        if (isCurrentLocationActive) {
            setTimeout(() => {
                zoomToCurrentLocation();
            }, 0);
        }
    }, [isCurrentLocationActive]);

    useEffect(() => {
        if (tour && tour.station) {
            zoomTo(tour.station.id);
        }
    }, [tour]);

    //Moves to search item in map whenever we click on search result item in search panel
    useEffect(() => {
        if (activeSearchId) {
            zoomToSearchEle(activeSearchId);
        }
    }, [activeSearchId]);

    useEffect(() => {
        if (!stations.length || !isZoneFirstTimeRender) {
            return;
        }
        !hasInitialMapParams && zoomToCurrentLocation();
        setIsZoneFirstTimeRender(false);
    }, [stations, isZoneFirstTimeRender, hasInitialMapParams]);

    //Centers drawer opened to station to center of the map
    //Its order has to be at the end so that on map load selected station given more priority to zoom than current location
    useEffect(() => {
        //We shoudn't center on first time load with deep link, but from next time it should auto center
        const canCenterSelectedStation = selectedStation && initialSelectedStation !== selectedStation.id;
        if (canCenterSelectedStation) {
            zoomToSelectedStation(selectedStation);
            setInitialSelectedStation('');
        }
    }, [selectedStation, initialSelectedStation]);

    return (
        <MapLayout>
            <Lables />
            <Backgrounds />
            <TransformComponent
                wrapperStyle={{
                    cursor: 'grab'
                }}
                contentStyle={{
                    width: `max(${highestAbsolutePosition.x}px, 100%)`,
                    height: `max(${highestAbsolutePosition.y}px, 100%)`
                }}
            >
                <Map id="zone-map">
                    {tour?.station && isCurrentTourItem(TourType.STATION_AND_DRAWERS) ? (
                        <TourActiveDrawerAndStationElement
                            data-testid="tour-station-and-drawer-element"
                            position={tour.station.getAbsolutePosition()}
                            withTimeline={!!tour.station.getTimeline()}
                        />
                    ) : null}
                    <PathDescriptions>
                        {paths.map(path => (
                            <PathDescription key={path.getId()} path={path} />
                        ))}
                    </PathDescriptions>
                    <StationsWrapper id="stations-wrapper">
                        {stations.map(station => (
                            <StationRenderer key={station.getId()} station={station} />
                        ))}
                    </StationsWrapper>
                    <RenderPaths paths={paths} selectedPath={selectedPath} />
                </Map>
            </TransformComponent>
            <Footer />
            <Actions />
        </MapLayout>
    );
};
